import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Box,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import {
  ReportProblemOutlined as ReportIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useUserContext } from "../Contexts/UserContext";
import { useShipSettings } from "../Contexts/ShipSettingsContext";
import { getShipName } from "../utils/domainSelectors";

const FeedbackBanner = () => {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useUserContext();
  const theme = useTheme();

  const userInfo = `${user?.userName || "Unknown"} (${user?.firstName || ""} ${
    user?.lastName || ""
  })`;
  const { shipData } = useShipSettings();

  const handleSubmit = () => {
    if (!navigator.onLine) {
      alert("No internet connection. Please try again later.");
      return;
    }
    setSending(true);

    emailjs
      .send(
        "service_gz25dhr",
        "template_kb5mm5e",
        { feedback, user: userInfo, ship: getShipName(shipData) || "Unknown" },
        "ZqQl9Axlm7CQDvMcX"
      )
      .then(() => {
        alert("Thank you for your feedback!");
        setFeedback("");
        setOpen(false);
      })
      .catch((error) => {
        alert("Failed to send feedback: " + error.text);
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          bgcolor: alpha(theme.palette.grey[900], 0.95), // Mörkare, semitransparent marin/svart
          backdropFilter: "blur(4px)", // Modern glas-effekt
          color: "white",
          px: 3,
          py: 1.5,
          zIndex: 1300,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          justifyContent="center"
          sx={{ maxWidth: 1200, width: "100%" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReportIcon
              sx={{ color: theme.palette.error.main, fontSize: 20 }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, letterSpacing: 0.5, opacity: 0.9 }}
            >
              BETA TEST PHASE: Help us improve the system by reporting issues.
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="error"
            onClick={() => setOpen(true)}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: "0.7rem",
              px: 3,
              borderRadius: 1,
              boxShadow: "none",
              "&:hover": { boxShadow: "none", bgcolor: "error.dark" },
            }}
          >
            {sending ? "Sending..." : "Report Issue"}
          </Button>
        </Stack>
      </Box>

      {/* RAPPORT-DIALOG */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Report an Issue
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Please describe the bug or issue as detailed as possible.
            Information about your vessel and account will be included
            automatically.
          </Typography>
          <TextField
            autoFocus
            label="Description"
            placeholder="What happened? In which section?"
            fullWidth
            multiline
            minRows={5}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: alpha(theme.palette.action.hover, 0.05),
              },
            }}
          />
        </DialogContent>
        <Divider sx={{ borderStyle: "dashed" }} />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!feedback.trim() || sending}
            startIcon={<SendIcon />}
            sx={{ fontWeight: 700, px: 3 }}
          >
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackBanner;
